/**
 * Fleet ROI Calculator: payback on retrofitting an existing cart fleet.
 *
 * Step 1 collects the operator's fleet inputs, step 2 compares the legacy
 * fleet against the retrofitted fleet over the retrofit lifespan. Shared form,
 * formatting, and chart plumbing come from {@link RoiCalculatorElement}; this
 * module owns only the retrofit math and its chart configs.
 *
 * @module fleet-roi-calculator
 */

import { RoiCalculatorElement } from '@theme/roi-calculator-core';

/** Hours of annual hands-on time a retrofitted cart still needs. */
const ASSUMED_RETRO_LABOR_HOURS_PER_CART = 2.4;

/** Annual damage cost per cart attributable to towing, when towing is in use. */
const TOW_DAMAGE_COST_PER_CART_PER_YEAR = 600;

/** Annual turf/grounds damage cost per cart, when towing is in use. */
const GROUNDS_DAMAGE_COST_PER_CART_PER_YEAR = 120;

const LEGACY_COLOR = '#5f6f64';
const RETRO_COLOR = '#FAB018';
const BREAKDOWN_COLORS = ['#1f8f3a', '#3ea0e8', '#f39f3d', '#8759c6', '#17a2a0'];

const BREAKDOWN_LABELS = ['Charging', 'Labor', 'Towing', 'Battery', 'Grounds'];

class FleetRoiCalculator extends RoiCalculatorElement {
  static refAttribute = 'data-fleet-roi';

  static inputKeys = [
    'fleetSize',
    'hourlyLaborCost',
    'dailyChargingCost',
    'towCarts',
    'batteryReplacementCost'
  ];

  static baselineKeys = [
    'legacyLaborPerCart',
    'legacyBatteryInterval',
    'retrofitKitCost',
    'postRetroDailyCharging',
    'postRetroAnnualMaintenance',
    'retrofitLifespan'
  ];

  static stringKeys = ['towCarts'];

  static fallbackDefaults = {
    fleetSize: 60,
    hourlyLaborCost: 12,
    dailyChargingCost: 1.5,
    towCarts: 'yes',
    batteryReplacementCost: 1500,
    legacyLaborPerCart: 363.54,
    legacyBatteryInterval: 3,
    retrofitKitCost: 2999,
    postRetroDailyCharging: 0.05,
    postRetroAnnualMaintenance: 15,
    retrofitLifespan: 10
  };

  /**
   * Turns the raw field values into every figure the results step displays.
   * @returns {object} annual costs per scenario plus the derived ROI metrics.
   */
  buildMetrics(fields) {
    const {
      fleetSize,
      hourlyLaborCost,
      dailyChargingCost,
      batteryReplacementCost,
      legacyLaborPerCart,
      legacyBatteryInterval,
      retrofitKitCost,
      postRetroDailyCharging,
      postRetroAnnualMaintenance,
      retrofitLifespan
    } = fields;

    const tows = fields.towCarts === 'yes';

    const legacyCharging = fleetSize * dailyChargingCost * 365;
    const retroCharging = fleetSize * postRetroDailyCharging * 365;

    const legacyLabor = fleetSize * legacyLaborPerCart;
    const retroLabor =
      fleetSize * (postRetroAnnualMaintenance + hourlyLaborCost * ASSUMED_RETRO_LABOR_HOURS_PER_CART);

    const legacyTowDamage = tows ? fleetSize * TOW_DAMAGE_COST_PER_CART_PER_YEAR : 0;
    const legacyGroundsDamage = tows ? fleetSize * GROUNDS_DAMAGE_COST_PER_CART_PER_YEAR : 0;

    const legacyBattery =
      legacyBatteryInterval > 0 ? (fleetSize * batteryReplacementCost) / legacyBatteryInterval : 0;

    // A retrofitted cart is towed, has no lead-acid pack, and is under warranty.
    const retroTowDamage = 0;
    const retroGroundsDamage = 0;
    const retroBattery = 0;

    const legacyTotal = legacyCharging + legacyLabor + legacyTowDamage + legacyGroundsDamage + legacyBattery;
    const retroTotal = retroCharging + retroLabor + retroTowDamage + retroGroundsDamage + retroBattery;

    const years = Math.max(1, Math.round(retrofitLifespan));
    const initialInvestment = fleetSize * retrofitKitCost;
    const annualSavings = legacyTotal - retroTotal;
    const lifetimeGross = annualSavings * retrofitLifespan;
    const lifetimeNet = lifetimeGross - initialInvestment;

    const costReduction = legacyTotal > 0 ? (annualSavings / legacyTotal) * 100 : 0;
    const roiMultiple = initialInvestment > 0 ? lifetimeNet / initialInvestment : 0;

    const paybackRaw = annualSavings > 0 ? initialInvestment / annualSavings : Number.POSITIVE_INFINITY;
    const paybackYears = Number.isFinite(paybackRaw) ? Math.ceil(paybackRaw) : NaN;

    return {
      years,
      retrofitLifespan,
      legacyCharging,
      legacyLabor,
      legacyTowDamage,
      legacyGroundsDamage,
      legacyBattery,
      legacyTotal,
      retroCharging,
      retroLabor,
      retroTowDamage,
      retroGroundsDamage,
      retroBattery,
      retroTotal,
      initialInvestment,
      annualSavings,
      lifetimeGross,
      lifetimeNet,
      costReduction,
      roiMultiple,
      paybackYears
    };
  }

  calculate() {
    const metrics = this.buildMetrics(this.readFields());
    const { years, annualSavings, legacyTotal, retroTotal, paybackYears, roiMultiple } = metrics;

    const money = this.fmtMoney.bind(this);
    const yearsFmt = this.fmtYears.bind(this);

    this.updateCell('outNetInitial', metrics.initialInvestment, money);
    this.updateCell('outAnnualSavings', annualSavings, money);
    this.updateCell('outAnnualSavingsOps', annualSavings, money);
    this.updateCell('outAnnualSavingsTop', annualSavings, money);
    this.updateCell('outLifetimeNet', metrics.lifetimeNet, money);
    this.updateCell('outLifetimeNetTop', metrics.lifetimeNet, money);
    this.updateCell('outPayback', paybackYears, yearsFmt);
    this.updateCell('outPaybackTop', paybackYears, yearsFmt);

    this.updateCell('outLegacyTenYear', legacyTotal * years, money);
    this.updateCell('outRetroTenYear', retroTotal * years, money);
    this.updateCell('outTenYearSavings', annualSavings * years, money);
    this.updateCell('outTenYearSavingsBadge', annualSavings * years, this.fmtMoneyCompact.bind(this));

    this.updateCellsText(
      ['outLifetimeYears', 'outLifetimeYearsSummary', 'outLifetimeNetYears', 'outRoiYears'],
      String(years)
    );

    this.updateCellsText(
      ['outPaybackMonths', 'outPaybackMonthsSummary'],
      Number.isFinite(paybackYears) ? String(Math.max(0, paybackYears * 12)) : 'N/A'
    );

    const roiText = Number.isFinite(roiMultiple) ? `${roiMultiple.toFixed(2)}x` : 'N/A';
    this.updateCellText('outRoiMultipleTop', Number.isFinite(roiMultiple) ? `${roiText} ROI` : 'N/A');
    this.updateCellText('outRoiMultipleSummary', roiText);
    this.updateCellText('outRoiPct', this.fmtPercent(Math.max(0, roiMultiple * 100), 0));
    this.updateCellText('outCostReductionPct', this.fmtPercent(Math.max(0, metrics.costReduction)));

    const costReductionBar = this.ref('outCostReductionBar');
    if (costReductionBar) {
      costReductionBar.style.width = `${Math.min(100, Math.max(0, metrics.costReduction))}%`;
    }

    this.renderCharts(metrics);
  }

  renderCharts(metrics) {
    FleetRoiCalculator.ensureChartPlugins();

    this.#renderCostTrend(metrics);
    this.#renderAnnualOps(metrics);
    this.#renderBreakdowns(metrics);
  }

  /** Cumulative legacy vs retrofitted spend across the retrofit lifespan. */
  #renderCostTrend({ years, legacyTotal, retroTotal }) {
    const labels = Array.from({ length: years }, (_, index) => String(index + 1));

    this.renderChart('costTrend', 'chartCostTrend', {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Legacy Fleet (Current)',
            data: labels.map((_, index) => legacyTotal * (index + 1)),
            borderColor: LEGACY_COLOR,
            backgroundColor: 'rgba(95, 111, 100, 0.14)',
            borderWidth: 3,
            tension: 0.25,
            fill: false,
            pointRadius: 3
          },
          {
            label: 'Retrofitted Fleet (New)',
            data: labels.map((_, index) => retroTotal * (index + 1)),
            borderColor: RETRO_COLOR,
            backgroundColor: 'rgba(31, 143, 58, 0.15)',
            borderWidth: 3,
            tension: 0.25,
            fill: false,
            pointRadius: 3
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: { display: false },
          legend: {
            position: 'top',
            align: 'start',
            labels: { usePointStyle: true, pointStyle: 'line' }
          },
          tooltip: { callbacks: { label: this.moneyTooltipLabel } }
        },
        scales: {
          y: { ticks: { callback: this.moneyTick } }
        }
      }
    });
  }

  /** Side-by-side annual cost per operating category, empty rows dropped. */
  #renderAnnualOps(metrics) {
    const categories = [
      { label: 'Charging', legacy: metrics.legacyCharging, retro: metrics.retroCharging },
      { label: 'Labor', legacy: metrics.legacyLabor, retro: metrics.retroLabor },
      { label: 'Towing', legacy: metrics.legacyTowDamage, retro: metrics.retroTowDamage },
      { label: 'Grounds', legacy: metrics.legacyGroundsDamage, retro: metrics.retroGroundsDamage },
      { label: 'Battery', legacy: metrics.legacyBattery, retro: metrics.retroBattery }
    ].filter((category) => category.legacy > 0 || category.retro > 0);

    const barStyle = {
      borderRadius: 0,
      borderSkipped: false,
      barThickness: 16,
      maxBarThickness: 34,
      categoryPercentage: 0.9,
      barPercentage: 0.8
    };

    this.renderChart('annualOps', 'chartAnnualOps', {
      type: 'bar',
      data: {
        labels: categories.map((category) => category.label),
        datasets: [
          {
            label: 'Legacy',
            data: categories.map((category) => category.legacy),
            backgroundColor: 'rgba(95, 111, 100, 0.85)',
            ...barStyle
          },
          {
            label: 'Retrofitted',
            data: categories.map((category) => category.retro),
            backgroundColor: RETRO_COLOR,
            ...barStyle
          }
        ]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            color: '#132218',
            anchor: 'end',
            align: 'end',
            clamp: true,
            font: { weight: '700', size: 11 },
            formatter: (value) => this.fmtMoney(value)
          },
          legend: { display: false },
          tooltip: { callbacks: { label: this.moneyTooltipLabel } }
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: { callback: this.moneyTick },
            grid: { color: 'rgba(89, 116, 95, 0.14)' }
          },
          y: { grid: { display: false } }
        }
      }
    });
  }

  /** Paired doughnuts showing where each fleet's annual spend goes. */
  #renderBreakdowns(metrics) {
    const series = {
      legacy: [
        metrics.legacyCharging,
        metrics.legacyLabor,
        metrics.legacyTowDamage,
        metrics.legacyBattery,
        metrics.legacyGroundsDamage
      ],
      retro: [
        metrics.retroCharging,
        metrics.retroLabor,
        metrics.retroTowDamage,
        metrics.retroBattery,
        metrics.retroGroundsDamage
      ]
    };

    const config = (values) => ({
      type: 'doughnut',
      data: {
        labels: BREAKDOWN_LABELS,
        datasets: [{ data: values, backgroundColor: BREAKDOWN_COLORS, borderWidth: 0 }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1,
        cutout: '55%',
        plugins: {
          legend: {
            position: 'bottom',
            align: 'start',
            labels: {
              boxWidth: 24,
              boxHeight: 10,
              padding: 14,
              font: { size: 12, weight: '400' },
              filter: (legendItem) => values[legendItem.index] > 0
            }
          },
          tooltip: {
            callbacks: { label: (context) => `${context.label}: ${this.fmtMoney(context.raw)}` }
          },
          datalabels: {
            color: '#ffffff',
            font: { weight: '700', size: 11 },
            formatter(value, context) {
              const total = context.chart.data.datasets[0].data.reduce((sum, item) => sum + item, 0);
              if (!total || value <= 0) {
                return '';
              }
              const percent = (value / total) * 100;
              return percent >= 6 ? `${percent.toFixed(0)}%` : '';
            }
          }
        }
      }
    });

    this.renderChart('legacyBreakdown', 'chartLegacyBreakdown', config(series.legacy));
    this.renderChart('retroBreakdown', 'chartRetroBreakdown', config(series.retro));
  }
}

if (!customElements.get('fleet-roi-calculator')) {
  customElements.define('fleet-roi-calculator', FleetRoiCalculator);
}
